import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Tooltip,
  Badge,
  Avatar,
  useTheme,
  InputAdornment,
  Menu,
  ListItemIcon,
  ListItemText,
  Fab,
  LinearProgress
} from '@mui/material';
import {
  Work as WorkIcon,
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  PlayArrow as ClockInIcon,
  Stop as ClockOutIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Route as RouteIcon,
  GpsFixed as GpsIcon,
  Schedule as ScheduleIcon,
  CheckCircle,
  Warning as WarningIcon,
  AccessTime as TimeIcon,
  People as CrewIcon,
  LocalShipping as TruckIcon,
  Today as TodayIcon,
  History as HistoryIcon,
  MoreVert as MoreVertIcon,
  Map as MapIcon,
  CalendarMonth as CalendarIcon
} from '@mui/icons-material';
import { makeApiCall } from '../../config/api';
import { formatScheduledDate, isScheduledDateToday } from '../../utils/dateUtils';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import JobDetailView from './JobDetailView';
import CreateJobDialog from './CreateJobDialog';
import RouteViewer from './RouteViewer';
import LiveTracking from './LiveTracking';
import CalendarView from './CalendarView';

const JobManagement = ({ onNavigateToJob, initialFilter, initialView }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const { t } = useTranslation();
  
  // State management
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // View state. TKT-181: accept an initialView prop so callers (eg the
  // Operations dashboard's "Full Live Tracking" link) can drop the user
  // straight onto the right tab instead of always landing on Jobs.
  const VALID_VIEWS = ['jobs', 'routes', 'tracking', 'calendar'];
  const [currentView, setCurrentView] = useState(
    VALID_VIEWS.includes(initialView) ? initialView : 'jobs'
  );
  const [selectedJobId, setSelectedJobId] = useState(null);
  
  // Jobs table state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalJobs, setTotalJobs] = useState(0);
  
  // Filters - apply dashboard drilldown defaults if provided
  const [filters, setFilters] = useState({
    search: '',
    job_type: initialFilter?.jobType || '',
    status: initialFilter?.status || '',
    truck_id: '',
    date_from: '',
    date_to: '',
    assigned_user_id: ''
  });
  
  // Sorting
  const [sortBy, setSortBy] = useState('scheduled_date');
  const [sortOrder, setSortOrder] = useState('DESC');
  
  // Dialogs
  const [createJobOpen, setCreateJobOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuJob, setMenuJob] = useState(null);

  // TKT-239: office-driven "Mark Complete" confirmation for construction
  // jobs. Held separately from the kebab so the menu can close while the
  // confirmation modal stays open.
  const [markCompleteJob, setMarkCompleteJob] = useState(null);
  const [markCompleteSubmitting, setMarkCompleteSubmitting] = useState(false);

  // TKT-221: office roles that may change a construction job's status.
  // Keyed on user_type until the 5-tier role model lands.
  const officeRoles = ['admin', 'superadmin', 'office', 'sales'];
  const canMarkComplete = officeRoles.includes(user?.user_type);
  
  // Dashboard stats
  const [stats, setStats] = useState({});
  
  // Lookup data
  const [trucks, setTrucks] = useState([]);
  const [employees, setEmployees] = useState([]);

  // ====================================
  // DATA FETCHING
  // ====================================

  const fetchJobs = async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        sort_by: sortBy,
        sort_order: sortOrder,
        ...filters
      });

      // Remove empty filters
      Object.keys(filters).forEach(key => {
        if (!filters[key]) {
          params.delete(key);
        }
      });

      const response = await makeApiCall(`api/jobs?${params}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch jobs');
      }

      setJobs(data.jobs || []);
      setTotalJobs(data.pagination?.total || 0);

    } catch (error) {
      console.error('Error fetching jobs:', error);
      setError('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await makeApiCall('api/jobs/stats/dashboard');
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching job stats:', error);
    }
  };

  const fetchLookups = async () => {
    try {
      const [trucksRes, employeesRes] = await Promise.all([
        makeApiCall('api/routes/trucks'),
        makeApiCall('api/routes/lookups/available-employees')
      ]);

      if (trucksRes.ok) {
        const trucksData = await trucksRes.json();
        setTrucks(trucksData.trucks || []);
      }

      if (employeesRes.ok) {
        const employeesData = await employeesRes.json();
        setEmployees(employeesData.available_employees || []);
      }
    } catch (error) {
      console.error('Error fetching lookups:', error);
    }
  };

  // ====================================
  // EVENT HANDLERS
  // ====================================

  const handleJobAction = async (action, job) => {
    try {
      switch (action) {
        case 'clock-in':
          // Get user location for clock in
          if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(async (position) => {
              const response = await makeApiCall(`api/jobs/${job.id}/clock-in`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  latitude: position.coords.latitude,
                  longitude: position.coords.longitude,
                })
              });

              if (response.ok) {
                setSuccess(t('jobs.clockedInSuccessfully'));
                fetchJobs();
              } else {
                const errorData = await response.json();
                setError(errorData.error || t('jobs.failedToClockIn'));
              }
            });
          } else {
            setError(t('common.error'));
          }
          break;
          
        case 'clock-out':
          if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(async (position) => {
              const response = await makeApiCall(`api/jobs/${job.id}/clock-out`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  latitude: position.coords.latitude,
                  longitude: position.coords.longitude,
                })
              });

              if (response.ok) {
                setSuccess(t('jobs.clockedOutSuccessfully'));
                fetchJobs();
              } else {
                const errorData = await response.json();
                setError(errorData.error || t('jobs.failedToClockOut'));
              }
            });
          }
          break;
          
        case 'view':
          setSelectedJobId(job.id);
          if (onNavigateToJob) {
            onNavigateToJob(job.id);
          }
          break;
          
        default:
          break;
      }
    } catch (error) {
      console.error(`Error performing ${action}:`, error);
      setError(`Failed to ${action}`);
    }
    handleMenuClose();
  };

  const handleMenuOpen = (event, job) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuJob(job);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuJob(null);
  };

  // TKT-239: open the confirmation modal for the job in the kebab, then
  // close the menu. The actual completion only happens on confirm.
  const openMarkComplete = (job) => {
    setMarkCompleteJob(job);
    handleMenuClose();
  };

  const submitMarkComplete = async () => {
    if (!markCompleteJob) return;
    try {
      setMarkCompleteSubmitting(true);
      const response = await makeApiCall(`api/jobs/${markCompleteJob.id}/mark-complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || t('jobs.markCompleteFailed'));
      }
      setSuccess(data.message || t('jobs.markCompleteSuccess'));
      setMarkCompleteJob(null);
      fetchJobs();
      fetchStats();
    } catch (error) {
      console.error('Error marking job complete:', error);
      setError(error.message || t('jobs.markCompleteFailed'));
    } finally {
      setMarkCompleteSubmitting(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(0); // Reset to first page when filters change
  };

  // ====================================
  // UTILITY FUNCTIONS
  // ====================================

  const getJobStatusColor = (job) => {
    if (job.is_complete) return 'success';
    if (job.clock_in_time && !job.clock_out_time) return 'warning';
    if (job.requires_approval) return 'error';
    return 'default';
  };

  const getJobStatusText = (job) => {
    if (job.is_complete) return t('jobs.completed');
    if (job.clock_in_time && !job.clock_out_time) return t('jobs.inProgress');
    if (job.requires_approval) return t('jobs.needsApproval');
    return t('jobs.scheduled');
  };

  const getJobTypeColor = (type) => {
    switch (type) {
      case 'summer': return 'primary';
      case 'winter': return 'info';
      case 'construction': return 'warning';
      default: return 'default';
    }
  };

  const formatDuration = (clockIn, clockOut) => {
    if (!clockIn || !clockOut) return '';
    const duration = new Date(clockOut) - new Date(clockIn);
    const hours = Math.floor(duration / (1000 * 60 * 60));
    const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  // ====================================
  // EFFECTS
  // ====================================

  useEffect(() => {
    fetchJobs();
  }, [page, rowsPerPage, filters, sortBy, sortOrder]);

  useEffect(() => {
    // Self-heal: materialize any of today's planned stops that the overnight
    // scheduler missed. Safe + idempotent. Then refresh the list + stats
    // so numbers line up with what the user expects.
    (async () => {
      try {
        const today = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        const iso = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
        const res = await makeApiCall('api/jobs/ensure-for-date', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ date: iso }),
        });
        if (res.ok) {
          const data = await res.json().catch(() => ({}));
          if ((data.created || 0) > 0) {
            // Something new was materialized - refresh both.
            fetchJobs();
            fetchStats();
          }
        }
      } catch (_) {
        // best-effort; don't block the page
      }
    })();

    fetchStats();
    fetchLookups();
  }, []);

  // ====================================
  // RENDER FUNCTIONS
  // ====================================

  const renderStatsCards = () => {
    const scheduledToday = stats.scheduled_today ?? stats.today_jobs ?? 0;
    const completedToday = stats.completed_today ?? stats.today_completed ?? 0;
    const inProgressNow = stats.in_progress_now ?? 0;
    const offScheduleToday = stats.scheduled_today_off_schedule ?? 0;

    const statsData = [
      {
        title: t('jobs.scheduledToday'),
        value: scheduledToday,
        icon: <TodayIcon />,
        color: 'info.main',
        subtitle: offScheduleToday > 0
          ? t('jobs.scheduledTodayBreakdown', { completed: completedToday, offSchedule: offScheduleToday })
          : t('jobs.completedTodayCount', { count: completedToday }),
      },
      {
        title: t('jobs.inProgressNow'),
        value: inProgressNow,
        icon: <WorkIcon />,
        color: 'primary.main',
        subtitle: t('jobs.clockedInHelp'),
      },
      {
        title: t('jobs.pendingApprovals'),
        value: stats.pending_approvals || 0,
        icon: <WarningIcon />,
        color: 'warning.main',
        subtitle: t('jobs.requireAttention')
      },
      {
        title: t('metrics.efficiency'),
        value: `${Math.round(stats.avg_efficiency || 0)}%`,
        icon: <CheckCircle />,
        color: 'success.main',
        subtitle: t('jobs.averagePerformance')
      }
    ];

    return (
      <Grid container spacing={3} mb={3}>
        {statsData.map((stat, index) => (
          <Grid size={{ xs: 6, sm: 6, md: 3 }} key={index}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      {stat.title}
                    </Typography>
                    <Typography variant="h4" component="h2">
                      {stat.value}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {stat.subtitle}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: stat.color, width: 56, height: 56 }}>
                    {stat.icon}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderViewTabs = () => (
    <Box display="flex" gap={1} mb={3} flexWrap="wrap">
      <Button
        variant={currentView === 'jobs' ? 'contained' : 'outlined'}
        startIcon={<WorkIcon />}
        onClick={() => setCurrentView('jobs')}
      >
        {t('jobs.title')}
      </Button>
      <Button
        variant={currentView === 'calendar' ? 'contained' : 'outlined'}
        startIcon={<CalendarIcon />}
        onClick={() => setCurrentView('calendar')}
      >
        {t('jobs.calendar')}
      </Button>
      <Button
        variant={currentView === 'routes' ? 'contained' : 'outlined'}
        startIcon={<RouteIcon />}
        onClick={() => setCurrentView('routes')}
        aria-label={t('jobs.todaysSchedule')}
      >
        {t('jobs.todaysSchedule')}
      </Button>
      <Button
        variant={currentView === 'tracking' ? 'contained' : 'outlined'}
        startIcon={<GpsIcon />}
        onClick={() => setCurrentView('tracking')}
      >
        {t('jobs.liveTracking')}
      </Button>
    </Box>
  );

  const renderFilters = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              label={t('jobs.searchJobs')}
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('jobs.jobType')}</InputLabel>
              <Select
                value={filters.job_type}
                label={t('jobs.jobType')}
                onChange={(e) => handleFilterChange('job_type', e.target.value)}
              >
                <MenuItem value="">{t('jobs.allTypes')}</MenuItem>
                <MenuItem value="summer">{t('jobs.summer')}</MenuItem>
                <MenuItem value="winter">{t('jobs.winter')}</MenuItem>
                <MenuItem value="construction">{t('jobs.construction')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('jobs.status')}</InputLabel>
              <Select
                value={filters.status}
                label={t('jobs.status')}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="">{t('jobs.allStatus')}</MenuItem>
                <MenuItem value="active">{t('customers.active')}</MenuItem>
                <MenuItem value="completed">{t('jobs.completed')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('jobs.truck')}</InputLabel>
              <Select
                value={filters.truck_id}
                label={t('jobs.truck')}
                onChange={(e) => handleFilterChange('truck_id', e.target.value)}
              >
                <MenuItem value="">{t('jobs.allTrucks')}</MenuItem>
                {trucks.map((truck) => (
                  <MenuItem key={truck.id} value={truck.id}>
                    {truck.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <TextField
              fullWidth
              type="date"
              label={t('jobs.dateFrom')}
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 1 }}>
            <Tooltip title={t('common.refresh')}>
              <IconButton onClick={fetchJobs} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderJobsTable = () => (
    <Card>
      <CardContent>
        {/* Desktop Table */}
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('jobs.jobNumber')}</TableCell>
                  <TableCell>{t('jobs.customer')}</TableCell>
                  <TableCell>{t('common.type')}</TableCell>
                  <TableCell>{t('jobs.truck')}</TableCell>
                  <TableCell>{t('jobs.scheduledDate')}</TableCell>
                  <TableCell>{t('jobs.status')}</TableCell>
                  <TableCell>{t('jobs.duration')}</TableCell>
                  <TableCell>{t('jobs.crew')}</TableCell>
                  <TableCell align="center">{t('quotes.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <LinearProgress />
                      <Typography sx={{ mt: 1 }}>{t('jobs.loadingJobs')}</Typography>
                    </TableCell>
                  </TableRow>
                ) : jobs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography>{t('jobs.noJobsFound')}</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  jobs.map((job) => (
                    <TableRow 
                      key={job.id} 
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleJobAction('view', job)}
                    >
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {job.job_number}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {job.company_name || `${job.first_name} ${job.last_name}`}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {job.city}, {job.province}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={job.job_type}
                          color={getJobTypeColor(job.job_type)}
                          size="small"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <TruckIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {job.truck_name || t('jobs.unassigned')}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatScheduledDate(job.scheduled_date)}
                        </Typography>
                        {isScheduledDateToday(job.scheduled_date) && (
                          <Chip label={t('jobs.today')} size="small" color="info" sx={{ ml: 1 }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getJobStatusText(job)}
                          color={getJobStatusColor(job)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <TimeIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {formatDuration(job.clock_in_time, job.clock_out_time) || '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Badge badgeContent={job.assigned_employees_count || 0} color="primary">
                            <CrewIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                          </Badge>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, job)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Mobile Card View */}
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          {loading ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <LinearProgress />
              <Typography sx={{ mt: 1 }}>{t('jobs.loadingJobs')}</Typography>
            </Box>
          ) : jobs.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography>{t('jobs.noJobsFound')}</Typography>
            </Box>
          ) : (
            jobs.map((job) => (
              <Card 
                key={job.id} 
                sx={{ mb: 2, cursor: 'pointer' }}
                onClick={() => handleJobAction('view', job)}
              >
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {job.job_number}
                    </Typography>
                    <Box display="flex" gap={1}>
                      <Chip
                        label={getJobStatusText(job)}
                        color={getJobStatusColor(job)}
                        size="small"
                      />
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, job)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>
                  </Box>
                  
                  <Typography variant="body2" gutterBottom>
                    {job.company_name || `${job.first_name} ${job.last_name}`}
                  </Typography>
                  
                  <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                    <Chip
                      label={job.job_type}
                      color={getJobTypeColor(job.job_type)}
                      size="small"
                      sx={{ textTransform: 'capitalize' }}
                    />
                    <Typography variant="caption" color="textSecondary">
                      {formatScheduledDate(job.scheduled_date)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalJobs}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </CardContent>
    </Card>
  );

  const renderContent = () => {
    switch (currentView) {
      case 'calendar':
        // TKT-191: when the page is opened as Summer/Winter/Construction
        // Logs the calendar should be pre-filtered to that job type, not
        // show every other discipline's jobs alongside.
        return <CalendarView
          initialJobType={initialFilter?.jobType || ''}
          onNavigateToJob={(jobId) => {
            setSelectedJobId(jobId);
            setCurrentView('jobs');
          }}
        />;
      case 'routes':
        return <RouteViewer />;
      case 'tracking':
        return <LiveTracking />;
      default:
        return renderJobsTable();
    }
  };

  // TKT-191: dynamic page title so the user can tell at a glance whether
  // they landed on the generic Job Management page, Summer Logs, Winter
  // Logs, or Construction Logs. All four still share the same component
  // so layout fixes only have to land in one place.
  const headerTitle = (() => {
    const jt = initialFilter?.jobType;
    if (jt === 'summer') return t('navigation.summerLogs', 'Summer Logs');
    if (jt === 'winter') return t('navigation.winterLogs', 'Winter Logs');
    if (jt === 'construction') return t('navigation.constructionLogs', 'Construction Logs');
    return t('jobs.jobManagement');
  })();

  // ====================================
  // MAIN RENDER
  // ====================================

  return (
    <Box sx={{ p: 3 }} data-demo-target="page-jobs-root">
      {/* Header */}
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        mb={3}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={{ xs: 2, sm: 0 }}
      >
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            fontWeight: 'bold',
            color: '#000062',
            fontSize: { xs: '1.5rem', md: '2.125rem' }
          }}
        >
          {headerTitle}
        </Typography>
        {/* TKT-190: hide the "+ One-Off Visit" action on contexts where it
            does not apply. The button creates an ad-hoc summer/winter
            visit, which is meaningless on the Live Tracking view (a map
            of trucks) and on the Construction Logs page (construction
            jobs are scheduled work, not drop-in visits). */}
        {currentView !== 'tracking' && initialFilter?.jobType !== 'construction' && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateJobOpen(true)}
            sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
            aria-label={t('jobs.createOneOffVisit')}
          >
            {t('jobs.createOneOffVisit')}
          </Button>
        )}
      </Box>

      {/* Stats Cards */}
      {renderStatsCards()}

      {/* View Selection */}
      {renderViewTabs()}

      {/* Filters (only show for jobs view) */}
      {currentView === 'jobs' && renderFilters()}

      {/* Main Content */}
      {renderContent()}

      {/* Job Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleJobAction('view', menuJob)}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('jobs.viewDetails')}</ListItemText>
        </MenuItem>
        
        {menuJob && !menuJob.clock_in_time && (
          <MenuItem onClick={() => handleJobAction('clock-in', menuJob)}>
            <ListItemIcon>
              <ClockInIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText>{t('jobs.clockIn')}</ListItemText>
          </MenuItem>
        )}
        
        {menuJob && menuJob.clock_in_time && !menuJob.clock_out_time && (
          <MenuItem onClick={() => handleJobAction('clock-out', menuJob)}>
            <ListItemIcon>
              <ClockOutIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>{t('jobs.clockOut')}</ListItemText>
          </MenuItem>
        )}

        {/* TKT-239: construction jobs are office-managed and often never
            clock in/out, so the only completion path is this explicit
            office action. Hidden once the job is already complete. */}
        {menuJob && menuJob.job_type === 'construction' && !menuJob.is_complete
          && !menuJob.is_cancelled && canMarkComplete && (
          <MenuItem onClick={() => openMarkComplete(menuJob)}>
            <ListItemIcon>
              <CheckCircle fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText>{t('jobs.markComplete')}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* TKT-239: Mark Complete confirmation modal (no browser confirm). */}
      <Dialog
        open={Boolean(markCompleteJob)}
        onClose={() => !markCompleteSubmitting && setMarkCompleteJob(null)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="mark-complete-dialog-title"
      >
        <DialogTitle id="mark-complete-dialog-title">{t('jobs.markComplete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('jobs.markCompleteConfirm', {
              // TKT-239: construction rows often have a blank job_number,
              // so fall back to the customer name to avoid a dangling
              // "Mark construction job  as complete?".
              jobNumber: markCompleteJob?.job_number
                || markCompleteJob?.company_name
                || `${markCompleteJob?.first_name || ''} ${markCompleteJob?.last_name || ''}`.trim()
                || t('jobs.thisJob')
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setMarkCompleteJob(null)}
            disabled={markCompleteSubmitting}
            aria-label={t('common.cancel')}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={submitMarkComplete}
            disabled={markCompleteSubmitting}
            startIcon={<CheckCircle />}
            aria-label={t('jobs.markComplete')}
          >
            {markCompleteSubmitting ? t('common.loading') : t('jobs.markComplete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Job Dialog */}
      <CreateJobDialog
        open={createJobOpen}
        onClose={() => setCreateJobOpen(false)}
        onSuccess={(message) => {
          setSuccess(message);
          fetchJobs();
          fetchStats();
        }}
        trucks={trucks}
        employees={employees}
      />

      {/* Success/Error Messages */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess('')}
      >
        <Alert onClose={() => setSuccess('')} severity="success">
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
      >
        <Alert onClose={() => setError('')} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default JobManagement;
